'use client';

import { SpaceSendLookupAction, SpaceTransactionMarker } from '@/components/spaces/SpaceSendAction';
import { CategoryTile } from '@/components/CategoryTile';
import { TransactionLine } from '@/components/transactions/TransactionLine';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { type CategoryIndex, getCategoryMeta } from './categoryIndex';
import type { Transaction } from '@/lib/transactions/types';

interface Props {
  tx: Transaction;
  categoryIndex: CategoryIndex;
}

/**
 * Compact transaction row for use inside `FaturaDetailDrawer`. Smaller avatar
 * and metadata than the main `TransactionRow`; not collapsible.
 */
export function FaturaTransactionItem({ tx, categoryIndex }: Props) {
  const formatCurrency = useFormatCurrency();
  const cat = getCategoryMeta(categoryIndex, tx.categoryId);
  const dt = new Date(tx.date);
  const displayMoney = tx.originalMoney ?? {
    amount: tx.amount,
    currencyCode: tx.currencyCode ?? 'BRL',
  };
  return (
    <TransactionLine
      as="li"
      density="compact"
      leading={<CategoryTile meta={cat} size="sm" />}
      title={
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-foreground">{tx.merchant}</p>
          {tx.installment && (
            <span className="rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-2xs font-bold text-muted-foreground tabular-nums">
              {tx.installment.current}/{tx.installment.total}
            </span>
          )}
        </div>
      }
      meta={
        <>
          <p className="text-2xs text-muted-foreground">
            {cat.label} · {dt.toLocaleDateString('pt-BR')}
          </p>
          <SpaceTransactionMarker spaceExpenses={tx.spaceExpenses} />
          <SpaceSendLookupAction transactionId={tx.id} eligible={!tx.isExpected && tx.amount < 0 && !tx.isInternalTransfer && !tx.isCashFlowNeutral} spaceExpenses={tx.spaceExpenses} />
        </>
      }
      amount={
        // A fatura lists charges, so the amount reads unsigned — the fatura's own total carries the direction.
        <div className="shrink-0 text-right">
          <p className="font-mono text-sm font-semibold text-foreground tabular-nums">
            {formatCurrency(Math.abs(displayMoney.amount), { currency: displayMoney.currencyCode })}
          </p>
          {tx.originalMoney && (
            <p className="font-mono text-3xs text-text-tertiary tabular-nums">
              {formatCurrency(Math.abs(tx.amount), { currency: tx.currencyCode ?? 'BRL' })} na conta
            </p>
          )}
        </div>
      }
    />
  );
}
